import React, { useEffect, useState, useCallback } from "react";
import { BarChart3, Gauge, Info, FileText, CalendarRange } from "lucide-react";
import AnalyticsAdvanced from "../components/AnalyticsAdvanced";
import DayView from "../components/DayView";
import EquityCurve from "../components/EquityCurve";
import TradingReport from "../components/TradingReport";
import { analytics as mockAnalytics, behavioralScore as mockBehavioral, traderProfile as mockProfile, pnlBySymbol as mockPnlSym, pnlByDay as mockPnlDay, tradingSessions } from "../mock";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RANGES = [
  { label: "Today", days: 1 },
  { label: "7D", days: 7 },
  { label: "30D", days: 30 },
  { label: "90D", days: 90 },
  { label: "1Y", days: 365 },
  { label: "All", days: 0 },
];

// Radar chart for trader profile
const Radar = ({ data }) => {
  const size = 220, cx = size / 2, cy = size / 2, r = 82;
  const n = data.length;
  const pt = (val, i) => {
    const a = (Math.PI * 2 * i) / n - Math.PI / 2;
    const rr = (val / 100) * r;
    return [cx + rr * Math.cos(a), cy + rr * Math.sin(a)];
  };
  const axisPt = (i) => {
    const a = (Math.PI * 2 * i) / n - Math.PI / 2;
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
  };
  const labelPt = (i) => {
    const a = (Math.PI * 2 * i) / n - Math.PI / 2;
    return [cx + (r + 20) * Math.cos(a), cy + (r + 20) * Math.sin(a)];
  };
  const shape = data.map((d, i) => pt(d.value, i).join(",")).join(" ");
  return (
    <svg width={size} height={size} className="mx-auto block overflow-visible">
      {[0.33, 0.66, 1].map((f, k) => (
        <polygon key={k} className="ws-radar-grid"
          points={data.map((_, i) => { const [x, y] = axisPt(i); return `${cx + (x - cx) * f},${cy + (y - cy) * f}`; }).join(" ")} />
      ))}
      {data.map((_, i) => { const [x, y] = axisPt(i); return <line key={i} x1={cx} y1={cy} x2={x} y2={y} className="ws-radar-axis" />; })}
      <polygon className="ws-radar-shape" points={shape} />
      {data.map((d, i) => { const [x, y] = pt(d.value, i); return <circle key={i} cx={x} cy={y} r="3" className="ws-radar-dot" />; })}
      {data.map((d, i) => {
        const [x, y] = labelPt(i);
        return <text key={i} x={x} y={y} textAnchor="middle" dominantBaseline="middle" fontSize="9" fill="#8496b5">{d.axis}</text>;
      })}
    </svg>
  );
};

// Circular score ring
const ScoreRing = ({ score }) => {
  const R = 52, C = 2 * Math.PI * R;
  const off = C - (score / 100) * C;
  return (
    <svg width="130" height="130" className="mx-auto">
      <defs>
        <linearGradient id="wsRingGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#2563eb" /><stop offset="100%" stopColor="#38bdf8" />
        </linearGradient>
      </defs>
      <circle cx="65" cy="65" r={R} className="ws-ring-track" strokeWidth="10" fill="none" />
      <circle cx="65" cy="65" r={R} className="ws-ring-fill" strokeWidth="10" fill="none"
        strokeDasharray={C} strokeDashoffset={off} transform="rotate(-90 65 65)" />
      <text x="65" y="60" textAnchor="middle" fontSize="30" fontWeight="700" fill="#fff" fontFamily="Space Grotesk">{score}</text>
      <text x="65" y="80" textAnchor="middle" fontSize="10" fill="#8496b5">/ 100</text>
    </svg>
  );
};

const fmtMoney = (v) => `${v >= 0 ? "+" : ""}${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const Analytics = () => {
  const [rangeDays, setRangeDays] = useState(0); // 0 = All
  const [mode, setMode] = useState("preset"); // preset | custom
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [real, setReal] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [dayHover, setDayHover] = useState(null);
  const [dayViewOpen, setDayViewOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      let qs = "";
      if (mode === "custom") {
        if (!customStart || !customEnd) return;
        qs = `?start=${customStart}&end=${customEnd}`;
      } else {
        qs = rangeDays ? `?days=${rangeDays}` : "";
      }
      const res = await fetch(`${API}/mt5/analytics${qs}`, { credentials: "include" });
      setReal(res.ok ? await res.json() : null);
    } catch { setReal(null); }
  }, [rangeDays, mode, customStart, customEnd]);

  useEffect(() => { load(); }, [load]);
  // Keep analytics fresh while auto-sync runs in the background.
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const rangeLabel = mode === "custom"
    ? "Custom range"
    : (RANGES.find((r) => r.days === rangeDays)?.label === "All" ? "All time" : `Last ${RANGES.find((r) => r.days === rangeDays)?.label}`);

  const hasData = real && real.has_data;

  // Build a unified view from real MT5 data when available, otherwise sample data.
  const monthly = hasData
    ? real.monthly.map((m) => ({ label: m.label, value: fmtMoney(m.value), positive: m.value >= 0 }))
    : mockAnalytics.monthly.map((m) => ({ label: m.label, value: m.value, delta: m.delta }));

  const eqPts = hasData ? real.equity_curve : mockAnalytics.equityCurve.map((v, i) => ({ equity: v, pnl: i > 0 ? v - mockAnalytics.equityCurve[i - 1] : v }));
  const eqVals = eqPts.map((e) => e.equity);
  const eqMin = Math.min(0, ...eqVals);
  const eqMax = Math.max(...eqVals, 0.0001);

  const bestPairs = hasData
    ? real.by_symbol.slice(0, 4).map((s) => ({ pair: s.symbol, pnl: fmtMoney(s.pnl), trades: s.trades, win: s.win }))
    : mockAnalytics.bestPairs;

  const score = hasData ? real.behavioral.score : mockBehavioral.score;
  const scoreMetrics = hasData
    ? [
        { label: "Consistency", value: real.behavioral.consistency },
        { label: "Profit", value: real.behavioral.pf_score },
        { label: "Risk Mgmt", value: real.behavioral.risk_score },
        { label: "Activity", value: real.behavioral.activity },
      ]
    : mockBehavioral.metrics;

  const profile = hasData ? real.trader_profile : mockProfile;

  const pnlSym = hasData
    ? real.by_symbol.map((s) => ({ symbol: s.symbol, pnl: s.pnl, label: fmtMoney(s.pnl) }))
    : mockPnlSym;
  const maxPnl = Math.max(...pnlSym.map((s) => Math.abs(s.pnl)), 0.0001);

  const pnlDay = hasData ? real.by_day : mockPnlDay;
  const maxDay = Math.max(...pnlDay.map((d) => Math.abs(d.pnl)), 0.0001);

  const sessionList = hasData
    ? (real.advanced?.sessions || []).filter((s) => s.trades > 0).map((s) => ({ name: s.name, pnl: fmtMoney(s.pnl), rawPnl: s.pnl, win: s.win, trades: s.trades }))
    : tradingSessions.map((s) => ({ name: s.name, pnl: s.pnl, rawPnl: 1, win: s.win }));

  return (
    <div className="max-w-[1200px] mx-auto px-8 py-8">
      <div className="flex items-center gap-3 mb-2 ws-fade-up">
        <div className="ws-icon-chip w-9 h-9"><BarChart3 className="w-5 h-5" /></div>
        <span className="text-[11px] tracking-[0.28em] text-[color:var(--ws-muted)] uppercase">The Data Doesn't Lie</span>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 ws-fade-up" style={{ animationDelay: "40ms" }}>
        <h1 className="ws-heading text-[40px] font-bold ws-title-gradient">Analytics</h1>
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-lg overflow-hidden border border-[color:var(--ws-border)]" data-testid="analytics-range">
            {RANGES.map((r) => (
              <button
                key={r.label}
                onClick={() => { setMode("preset"); setRangeDays(r.days); }}
                data-testid={`analytics-range-${r.label.toLowerCase()}`}
                className={`px-3.5 py-1.5 text-[12px] font-semibold transition-colors ${mode === "preset" && rangeDays === r.days ? "bg-[color:var(--ws-blue)] text-white" : "text-[color:var(--ws-muted)] hover:text-white"}`}
              >
                {r.label}
              </button>
            ))}
            <button
              onClick={() => setMode("custom")}
              data-testid="analytics-range-custom"
              className={`px-3.5 py-1.5 text-[12px] font-semibold transition-colors ${mode === "custom" ? "bg-[color:var(--ws-blue)] text-white" : "text-[color:var(--ws-muted)] hover:text-white"}`}
            >
              Custom
            </button>
          </div>
          {mode === "custom" && (
            <div className="inline-flex items-center gap-2" data-testid="analytics-custom-inputs">
              <input type="date" value={customStart} max={customEnd || undefined} onChange={(e) => setCustomStart(e.target.value)}
                className="bg-[rgba(10,17,33,0.8)] border border-[color:var(--ws-border)] rounded-md text-[12px] text-white px-2 py-1.5 outline-none" data-testid="custom-start" />
              <span className="text-[color:var(--ws-muted)] text-[12px]">→</span>
              <input type="date" value={customEnd} min={customStart || undefined} onChange={(e) => setCustomEnd(e.target.value)}
                className="bg-[rgba(10,17,33,0.8)] border border-[color:var(--ws-border)] rounded-md text-[12px] text-white px-2 py-1.5 outline-none" data-testid="custom-end" />
            </div>
          )}
          <button
            onClick={() => setDayViewOpen((v) => !v)}
            className={`px-4 py-1.5 rounded-lg text-[12px] font-semibold inline-flex items-center gap-2 transition-all ${dayViewOpen ? "ws-btn-primary" : "ws-btn-ghost"}`}
            data-testid="ws-day-view-btn"
            title="Review your trading performance day by day"
          >
            <CalendarRange className="w-3.5 h-3.5" /> WS Day View
          </button>
          <button
            onClick={() => setReportOpen(true)}
            disabled={!hasData}
            className={`px-4 py-1.5 rounded-lg text-[12px] font-semibold inline-flex items-center gap-2 transition-all ${hasData ? "ws-btn-primary" : "opacity-40 cursor-not-allowed ws-btn-ghost"}`}
            data-testid="get-report-btn"
            title={hasData ? "Generate a report for the selected range" : "Connect MT5 and sync trades to enable"}
          >
            <FileText className="w-3.5 h-3.5" /> Get My Trading Report
          </button>
        </div>
      </div>

      {dayViewOpen ? (
        <div data-testid="ws-day-view-panel">
          <div className="mb-4">
            <h2 className="ws-heading text-xl font-bold">Day View</h2>
            <p className="text-[13px] text-[color:var(--ws-muted)]">Review your trading performance day by day — forex &amp; crypto pairs (XAUUSD, BTC, ETH…)</p>
          </div>
          <DayView />
        </div>
      ) : (
      <>
      {!hasData && (
        <div className="flex items-start gap-2 mb-6 rounded-lg p-3 ws-fade-up" style={{ background: "rgba(56,189,248,0.06)", border: "1px solid rgba(56,189,248,0.22)", animationDelay: "60ms" }} data-testid="analytics-sample-banner">
          <Info className="w-4 h-4 text-[color:var(--ws-cyan)] shrink-0 mt-0.5" />
          <span className="text-[12px] text-[color:var(--ws-text)] opacity-90">Showing sample analytics. Connect your MT5 account in the AI Journal to see your real equity curve and behavioral score here.</span>
        </div>
      )}

      {hasData && real.advanced && <AnalyticsAdvanced adv={real.advanced} totals={real.totals} />}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6 ws-fade-up" style={{ animationDelay: "80ms" }}>
        {monthly.map((m) => (
          <div key={m.label} className="ws-panel ws-panel-hover p-5">
            <div className="text-[12px] text-[color:var(--ws-muted)] mb-1">{m.label}</div>
            <div className={`ws-heading text-[26px] font-bold ${m.positive === false ? "ws-down" : m.positive ? "ws-up" : "text-white"}`}>{m.value}</div>
            {m.delta && <div className={`text-[12px] font-medium ${m.delta.startsWith("-") ? "ws-down" : "ws-up"}`}>{m.delta} vs last mo.</div>}
            {hasData && <div className="text-[11px] text-[color:var(--ws-muted)]">net P&amp;L</div>}
          </div>
        ))}
        {monthly.length === 0 && (
          <div className="ws-panel p-5 col-span-2 lg:col-span-4 text-center text-[13px] text-[color:var(--ws-muted)]">No completed trades in this range yet.</div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mb-4">
        <div className="ws-panel p-6 lg:col-span-2 ws-fade-up" style={{ animationDelay: "120ms" }}>
          <h3 className="ws-heading text-[17px] font-semibold mb-6">Equity Curve {hasData && <span className="text-[11px] text-[color:var(--ws-muted)] font-normal">· cumulative realized P&amp;L</span>}</h3>
          {hasData ? (
            <div className="h-[240px]"><EquityCurve daily={real.equity_daily || []} /></div>
          ) : (
            <div className="flex items-end gap-1.5 h-[220px]" data-testid="analytics-equity-curve">
              {eqVals.length === 0 && <div className="text-[13px] text-[color:var(--ws-muted)] m-auto">No trades to plot.</div>}
              {eqPts.map((e, i) => {
                const h = ((e.equity - eqMin) / (eqMax - eqMin)) * 100;
                return (
                  <div key={i} className="flex-1 h-full flex flex-col items-center justify-end gap-2" title={`${fmtMoney(e.equity)}`}>
                    <div className="w-full rounded-t" style={{ height: `${Math.max(2, h)}%`, background: (e.pnl >= 0 ? "linear-gradient(180deg,#38bdf8,#2563eb)" : "linear-gradient(180deg,#f87171,#7f1d1d)") }} />
                    {eqPts.length <= 14 && <span className="text-[9px] text-[color:var(--ws-muted)]">{i + 1}</span>}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="ws-panel p-6 ws-fade-up" style={{ animationDelay: "160ms" }}>
          <h3 className="ws-heading text-[17px] font-semibold mb-4">Best Pairs</h3>
          <div className="space-y-3">
            {bestPairs.map((p) => (
              <div key={p.pair} className="ws-price-cell p-3">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="ws-heading font-semibold text-white text-[14px]">{p.pair}</span>
                  <span className={`ws-heading font-semibold ${String(p.pnl).startsWith("-") ? "ws-down" : "ws-up"}`}>{p.pnl}</span>
                </div>
                <div className="flex items-center justify-between text-[11px] text-[color:var(--ws-muted)] mb-1.5">
                  <span>{p.trades} trades</span><span>{p.win}% win</span>
                </div>
                <div className="h-1.5 rounded-full bg-[rgba(120,140,180,0.18)] overflow-hidden">
                  <div className="ws-rank-fill h-full" style={{ width: `${p.win}%` }} />
                </div>
              </div>
            ))}
            {bestPairs.length === 0 && <div className="text-[13px] text-[color:var(--ws-muted)]">No symbols traded yet.</div>}
          </div>
        </div>
      </div>

      {/* Behavioral Score + Trader Profile radar */}
      <div className="grid lg:grid-cols-3 gap-4 mb-4">
        <div className="ws-panel p-6 ws-fade-up" style={{ animationDelay: "200ms" }}>
          <div className="flex items-center gap-2 mb-4">
            <Gauge className="w-4 h-4 text-[color:var(--ws-cyan)]" />
            <h3 className="ws-heading text-[17px] font-semibold">Behavioral Score</h3>
          </div>
          <ScoreRing score={score} />
          <div className="text-center mt-2 mb-4">
            {hasData
              ? <span className="text-[11px] text-[color:var(--ws-muted)]">Based on your win rate, profit factor &amp; consistency</span>
              : <><span className="ws-up text-[13px] font-semibold">▲ {mockBehavioral.delta} this month</span><span className="text-[11px] text-[color:var(--ws-muted)] ml-2">{mockBehavioral.trend}</span></>}
          </div>
          <div className="grid grid-cols-2 gap-2">
            {scoreMetrics.map((m) => (
              <div key={m.label} className="ws-price-cell p-2.5">
                <div className="text-[10px] text-[color:var(--ws-muted)]">{m.label}</div>
                <div className="ws-heading text-[16px] font-bold text-white">{m.value}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="ws-panel p-6 lg:col-span-2 ws-fade-up" style={{ animationDelay: "240ms" }}>
          <h3 className="ws-heading text-[17px] font-semibold mb-2">Trader Profile</h3>
          <div className="grid md:grid-cols-2 gap-4 items-center">
            <Radar data={profile} />
            <div className="space-y-2">
              {profile.map((t) => (
                <div key={t.axis}>
                  <div className="flex items-center justify-between text-[12px] mb-1">
                    <span className="text-[color:var(--ws-text)]">{t.axis}</span>
                    <span className="text-[color:var(--ws-muted)]">{t.value}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-[rgba(120,140,180,0.18)] overflow-hidden">
                    <div className="ws-rank-fill h-full" style={{ width: `${t.value}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* P&L by symbol / day + sessions */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="ws-panel p-6 ws-fade-up" style={{ animationDelay: "280ms" }}>
          <h3 className="ws-heading text-[17px] font-semibold mb-4">P&amp;L by Symbol</h3>
          <div className="space-y-3">
            {pnlSym.map((s) => {
              const pos = s.pnl >= 0;
              return (
                <div key={s.symbol} className="flex items-center gap-3">
                  <span className="text-[12px] text-[color:var(--ws-text)] w-16 shrink-0 truncate">{s.symbol}</span>
                  <div className="flex-1 h-4 rounded bg-[rgba(120,140,180,0.1)] overflow-hidden">
                    <div className="h-full rounded" style={{ width: `${(Math.abs(s.pnl) / maxPnl) * 100}%`, background: pos ? "linear-gradient(90deg,#2563eb,#38bdf8)" : "linear-gradient(90deg,#7f1d1d,#f87171)" }} />
                  </div>
                  <span className={`text-[12px] font-semibold w-16 text-right ${pos ? "ws-up" : "ws-down"}`}>{s.label}</span>
                </div>
              );
            })}
            {pnlSym.length === 0 && <div className="text-[13px] text-[color:var(--ws-muted)]">No data.</div>}
          </div>
        </div>

        <div className="ws-panel p-6 ws-fade-up relative" style={{ animationDelay: "320ms" }}>
          <h3 className="ws-heading text-[17px] font-semibold mb-4">P&amp;L by Day</h3>
          <div className="flex items-end justify-around h-[180px]" onMouseLeave={() => setDayHover(null)}>
            {pnlDay.map((d) => {
              const pos = d.pnl >= 0;
              const h = (Math.abs(d.pnl) / maxDay) * 70;
              return (
                <div key={d.day} className="flex flex-col items-center justify-center gap-2 h-full cursor-pointer group"
                  onMouseEnter={() => setDayHover(d)} data-testid={`pnl-day-${d.day.toLowerCase()}`}>
                  <div className="flex-1 flex items-end">
                    {pos && <div className="w-7 rounded-t transition-opacity group-hover:opacity-100 opacity-90" style={{ height: `${h}%`, background: "linear-gradient(180deg,#38bdf8,#2563eb)" }} />}
                  </div>
                  <div className="flex-1 flex items-start">
                    {!pos && <div className="w-7 rounded-b transition-opacity group-hover:opacity-100 opacity-90" style={{ height: `${h}%`, background: "linear-gradient(180deg,#f87171,#7f1d1d)" }} />}
                  </div>
                  <span className={`text-[11px] ${dayHover?.day === d.day ? "text-white font-semibold" : "text-[color:var(--ws-muted)]"}`}>{d.day}</span>
                </div>
              );
            })}
          </div>
          {dayHover && (
            <div className="absolute top-4 right-4 ws-panel px-3 py-2 rounded-lg shadow-xl pointer-events-none z-20" style={{ minWidth: 160 }} data-testid="pnl-day-tooltip">
              <div className="text-[11px] font-semibold text-white mb-1">
                {dayHover.day}{dayHover.date ? ` · ${new Date(dayHover.date + "T00:00:00Z").toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" })}` : dayHover.days_count > 1 ? ` · ${dayHover.days_count} days` : ""}
              </div>
              <div className="flex justify-between gap-4 text-[11px]"><span className="text-[color:var(--ws-muted)]">Net P&L</span><span className={dayHover.pnl >= 0 ? "ws-up" : "ws-down"}>{fmtMoney(dayHover.pnl)}</span></div>
              {dayHover.trades != null && <div className="flex justify-between gap-4 text-[11px]"><span className="text-[color:var(--ws-muted)]">Trades</span><span className="text-white">{dayHover.trades} ({dayHover.wins}W/{dayHover.losses}L)</span></div>}
              {dayHover.gross_profit != null && <div className="flex justify-between gap-4 text-[11px]"><span className="text-[color:var(--ws-muted)]">Profit / Loss</span><span className="text-white">{fmtMoney(dayHover.gross_profit)} / {fmtMoney(-Math.abs(dayHover.gross_loss))}</span></div>}
            </div>
          )}
        </div>

        <div className="ws-panel p-6 ws-fade-up" style={{ animationDelay: "360ms" }}>
          <h3 className="ws-heading text-[17px] font-semibold mb-4">Best Sessions {hasData && <span className="text-[11px] text-[color:var(--ws-muted)] font-normal">· UTC</span>}</h3>
          <div className="space-y-3">
            {sessionList.map((s) => {
              const pos = (s.rawPnl ?? 0) >= 0;
              return (
                <div key={s.name} className="ws-price-cell p-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="ws-heading font-semibold text-white text-[14px]">{s.name}</span>
                    <span className={`ws-heading font-semibold text-[14px] ${pos ? "ws-up" : "ws-down"}`}>{s.pnl}</span>
                  </div>
                  <div className="flex items-center justify-between text-[11px] text-[color:var(--ws-muted)] mb-1.5">
                    <span>{hasData ? `${s.trades} trades` : "Win rate"}</span><span>{s.win}% win</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-[rgba(120,140,180,0.18)] overflow-hidden">
                    <div className="ws-rank-fill h-full" style={{ width: `${s.win}%` }} />
                  </div>
                </div>
              );
            })}
            {sessionList.length === 0 && <div className="text-[13px] text-[color:var(--ws-muted)]">No session data.</div>}
          </div>
        </div>
      </div>
      </>
      )}

      {reportOpen && hasData && <TradingReport real={real} rangeLabel={rangeLabel} onClose={() => setReportOpen(false)} />}
    </div>
  );
};

export default Analytics;
